var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { injectable } from '../../decorators/injectable';
import { InjectorScope } from '../../core/injector';
/** The name of this module */
export const name = 'IntervalQueue';
/**
 * We want a function to return the specified type so we can
 * enforce uniqueness for keys in the queue.
 *
 * We are using seed plus the current time in milliseconds to
 * try and enforce uniqueness of keys with minimal performance
 * impact and prevention of collisions.
 */
export const getIntervalQueueKey = (seed) => `${Date.now()}_${seed}`;
let IntervalQueue = class IntervalQueue {
    constructor() {
        /** Handles maintaining a registry of inactive queues */
        this.inactive = new Map();
        /** The intervals tracking the delays and the callbacks */
        this.intervals = new Map();
        /** Registers all of the callbacks with the queue */
        this.callbacks = new Map();
        /** Tracks the delays that the callbacks are in */
        this.delays = new Map();
        /** Counter for number of interval cycles */
        this.intervalCount = 0;
        /** Counter for the number of registered callbacks */
        this.registeredCount = 0;
    }
    /**
       * Adds a callback to the interval queue
       */
    add({ callback, delay }) {
        // Generate an interval queue key with a random seed.
        const key = getIntervalQueueKey(this.registeredCount);
        // We've already got that callback registered with the queue.
        if (this.callbacks.has(key)) {
            return key;
        }
        // Once the seed is used, increment.
        this.registeredCount += 1;
        // Bind the callback
        this.callbacks.set(key, callback);
        // If the delay is not already registered, register it.
        if (!this.delays.has(delay)) {
            this.delays.set(delay, []);
        }
        this.delays
            .get(delay)
            .push(key);
        this.start(delay);
        return key;
    }
    /**
       * Handles kicking off the interval queues once they've been bound
       */
    start(delay) {
        // If the interval is already bound, don't rebind it
        if (this.intervals.has(delay)) {
            return;
        }
        this.intervals.set(delay, window.setInterval(this.process.bind(this, delay), delay));
    }
    /**
       * Handles executing the queued callbacks
       */
    process(delay) {
        // If there are no callbacks to process, break out
        if (this.delays.get(delay).length < 1) {
            return;
        }
        this.intervalCount += 1;
        this.delays.get(delay).forEach((key) => {
            this.callbacks.get(key)(this.intervalCount);
        });
    }
    /**
       * Remove a function from being called each time the repeating interval occurs
       */
    remove(key) {
        if (!this.callbacks.has(key)) {
            return;
        }
        let delay;
        // Find the delay that contains this key
        this.delays.forEach((keyList, mapDelay, delayMap) => {
            if (!keyList.includes(key)) {
                return;
            }
            // Pop it out of the loop
            delay = mapDelay;
            // Filter the removed key from the map
            delayMap.set(mapDelay, keyList.filter((entry) => entry !== key));
        });
        // Add the item to the inactives
        this.inactive.set(key, { delay, callback: this.callbacks.get(key) });
        // Remove the callback from the actives
        this.callbacks.delete(key);
    }
    /**
       * Adds the inactive entry to the queue for processing
       */
    restart(key) {
        if (!this.inactive.has(key)) {
            return;
        }
        const inactive = this.inactive.get(key);
        // Inject the entry back to get processed
        this.callbacks.set(key, inactive.callback);
        this.delays.get(inactive.delay).push(key);
    }
};
IntervalQueue = __decorate([
    injectable({
        key: name,
        scope: InjectorScope.Singleton,
    })
], IntervalQueue);
export default IntervalQueue;
